/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */
import './MobileAccountsCostBasisTable.scss';

import { array, number, string, shape } from 'prop-types';
import { isEmpty } from 'lodash';
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { FormattedDate } from 'components/shared/Text/Text';
import { Quantity } from 'components/shared/Text/Text';
import Content from 'services/Content';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import DollarCell from 'components/layouts/Taxes/shared/DollarCell';
import GainLossCell from 'components/layouts/Taxes/shared/GainLossCell';

const CostBasisMethodCell = ({ method }) => (
  <td>
    <Link className="table-link" to="/profile/cost-basis">
      {method}
    </Link>
  </td>
);

const CostBasisCoverageTable = ({ costBasisMethod, data, label, view }) => (
  <table className={`mobile-accounts-cost-basis-coverage-table ${label}`}>
    <tbody>
      <tr>
        <td className="coverage-label">{label}</td>
      </tr>
      <tr>
        <td className="shares">Shares</td>
        <td className="shares-quantity">
          <Quantity>{data.shares}</Quantity>
        </td>
      </tr>
      <tr>
        <td className="market-value">Balance</td>
        <DollarCell amount={data.marketValue} />
      </tr>
      <tr>
        <td className="cost-basis-method">Cost Basis Method</td>
        <CostBasisMethodCell method={costBasisMethod} view={view} />
      </tr>
      <tr>
        <td className="cost-per-share">Cost Per Share</td>
        <DollarCell amount={data.costPerShare} />
      </tr>
      <tr>
        <td className="total-unrealized-gain-loss">
          Total Unrealized Gain/Loss
        </td>
        <GainLossCell
          amount={data.gainLossAmount}
          percent={data.gainLossPercent}
        />
      </tr>
    </tbody>
  </table>
);

CostBasisCoverageTable.propTypes = {
  costBasisMethod: string.isRequired,
  data: shape({
    costPerShare: number.isRequired,
    gainLossAmount: number.isRequired,
    gainLossPercent: number.isRequired,
    marketValue: number.isRequired,
    shares: number.isRequired
  }),
  label: string.isRequired
};

const MobileAccountsCostBasisTable = ({
  asOfDate,
  unrealizedCostBasisRows,
  view
}) => {
  const { accountType, rows } = unrealizedCostBasisRows;

  return (
    !isEmpty(rows) && (
      <div className="mobile-cost-basis-container">
        <div className="table-header">Cost Basis</div>
        <div className="table-date">
          As of <FormattedDate>{asOfDate}</FormattedDate>
        </div>
        <div className="mobile-accounts-cost-basis-account-type">
          {accountType}
        </div>
        <div className="mobile-accounts-cost-basis-account-rows">
          {rows &&
            rows.map(({ balance }, i) => {
              const { costBasisMethod, covered, noncovered, total } = balance;
              return (
                <Fragment key={i}>
                  <div className="mobile-accounts-cost-basis-table-row">
                    <CostBasisCoverageTable
                      costBasisMethod={costBasisMethod}
                      data={total}
                      label="TOTAL"
                      view={view}
                    />
                  </div>
                  <div className="horizontal-bar-separator"></div>
                  <div className="mobile-accounts-cost-basis-table-row">
                    <CostBasisCoverageTable
                      costBasisMethod={costBasisMethod}
                      data={covered}
                      label="Covered"
                      view={view}
                    />
                  </div>
                  <div className="horizontal-bar-separator"></div>
                  <div className="mobile-accounts-cost-basis-table-row">
                    <CostBasisCoverageTable
                      costBasisMethod={costBasisMethod}
                      data={noncovered}
                      label="Noncovered"
                      view={view}
                    />
                  </div>
                  <div className="horizontal-bar-separator"></div>
                </Fragment>
              );
            })}
          <div className="mobile-account-cost-basis-footer">
            <div className="realized-loss-gain">
              <Link
                to={{
                  pathname: '/taxes',
                  hash: '#cost-basis-table'
                }}
                className="account-realized-loss-gain-button">
                View Realized Gain/Loss
              </Link>
            </div>
            <DisclosureModal
              content={Content.accountDetailsCostBasisDisclosure}
            />
          </div>
        </div>
      </div>
    )
  );
};

MobileAccountsCostBasisTable.propTypes = {
  unrealizedCostBasisRows: array.isRequired
};

export default MobileAccountsCostBasisTable;
